import { Badge, Button } from '@fluentui/react-components';
import { Archive, FileCode, Pin, ShieldAlert, ShieldCheck, SlidersHorizontal } from 'lucide-react';

import { Migration, ReadinessRulesetState } from '../api/migrations';

// FD-206 — the wave-level readiness picture (FD-ADR-027 / FD-205).
//
// This panel exists because the ADR's safety argument is "watch one real wave in
// observe, then flip to enforce", and that is not a decision anyone can make by
// scrolling 2000 grid rows. The two numbers that matter are:
//
//   wouldBlockCount — how many devices WOULD be held if enforcement were on. This
//                     is the number you read before flipping.
//   heldCount       — how many are actually being withheld right now. Always 0
//                     under observe.
//
// Stating the mode explicitly is the whole point of the header: without it,
// "nothing is blocked" and "blocking is turned off" look identical in the grid,
// which is the worst possible ambiguity for a gate.

export interface ReadinessSummaryPanelProps {
  migration: Migration;
  // Filter affordance — the parent owns the filter state so the grid and this panel
  // cannot disagree about what is shown.
  heldFilterActive?: boolean;
  onToggleHeldFilter?: () => void;
  /**
   * FD-494 — suppress the inline verdict counts.
   *
   * Defaults to TRUE so every existing caller is unchanged. The campaign Devices
   * tab passes false because the same five numbers now sit directly above this
   * panel as metric tiles, and printing them twice 200px apart is not density, it
   * is a reader wondering which one to believe. Everything else the panel carries
   * — the enforcement sentence, the ruleset provenance, the blocking reasons and
   * the held-filter toggle — is unaffected, and the enforcement sentence in
   * particular must never be hidden: "nothing is blocked" and "blocking is turned
   * off" look identical without it (FD-205).
   */
  showStatusCounts?: boolean;
}

export default function ReadinessSummaryPanel({
  migration,
  heldFilterActive,
  onToggleHeldFilter,
  showStatusCounts = true,
}: ReadinessSummaryPanelProps) {
  const summary = migration.readinessSummary;
  if (!summary) return null;

  const enforcing = migration.readinessEnforcement === 'enforce';
  const { byStatus } = summary;
  const total =
    byStatus.pass + byStatus.warn + byStatus.unknown + byStatus.blocked + byStatus.notEvaluated;

  const reasons = Object.entries(summary.byBlockingReason ?? {}).sort((a, b) => b[1] - a[1]);

  return (
    // A labelled region, matching the tile row above it on the campaign Devices
    // tab (FD-494). Both surfaces use the same five words, so each needs an
    // identity a reader can name.
    <section aria-label="Preflight readiness" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {enforcing ? (
          <ShieldAlert size={16} strokeWidth={1.5} className="text-red-600" aria-hidden />
        ) : (
          <ShieldCheck size={16} strokeWidth={1.5} className="text-zinc-500" aria-hidden />
        )}
        <span className="font-display text-sm font-medium text-zinc-900">
          Preflight readiness
        </span>
        <Badge appearance="tint" color={enforcing ? 'danger' : 'subtle'}>
          {enforcing ? 'Enforcing' : 'Observe only'}
        </Badge>
        {migration.readinessPolicyVersion ? (
          <span className="font-mono text-xs text-zinc-500">
            policy {migration.readinessPolicyVersion}
          </span>
        ) : null}
      </div>

      <RulesetProvenance ruleset={migration.readinessRuleset} />

      <p className="text-sm text-zinc-600">
        {enforcing ? (
          <>
            <span className="font-medium text-zinc-900">{summary.heldCount}</span> of {total}{' '}
            {total === 1 ? 'device is' : 'devices are'} being held — their flight plans are
            withheld until the reported checks are remediated or an override is granted.
          </>
        ) : (
          <>
            Verdicts are recorded but <span className="font-medium text-zinc-900">no flight plan is
            being withheld</span>. If enforcement were turned on,{' '}
            <span className="font-medium text-zinc-900">{summary.wouldBlockCount}</span> of {total}{' '}
            {total === 1 ? 'device' : 'devices'} would be held.
          </>
        )}
      </p>

      {showStatusCounts ? (
        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <Stat label="Ready" value={byStatus.pass} />
          <Stat label="Warnings" value={byStatus.warn} />
          <Stat label="Not ready" value={byStatus.blocked} />
          <Stat label="Unknown" value={byStatus.unknown} />
          <Stat label="Not evaluated" value={byStatus.notEvaluated} />
          {summary.overriddenCount > 0 ? (
            <Stat label="Overridden" value={summary.overriddenCount} />
          ) : null}
        </dl>
      ) : summary.overriddenCount > 0 ? (
        // The overrides count has no tile of its own — an override is not a
        // verdict, it is a waiver ON one — so it survives the suppression.
        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <Stat label="Overridden" value={summary.overriddenCount} />
        </dl>
      ) : null}

      {reasons.length > 0 ? (
        <div className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wide text-zinc-500">Blocking reasons</span>
          <ul className="flex flex-wrap gap-2">
            {reasons.map(([code, count]) => (
              <li key={code}>
                <Badge appearance="outline" color="informative">
                  <span className="font-mono text-xs">{code}</span>
                  <span className="ml-1 text-zinc-600">{count}</span>
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {onToggleHeldFilter && summary.wouldBlockCount > 0 ? (
        <div>
          <Button size="small" appearance={heldFilterActive ? 'primary' : 'secondary'} onClick={onToggleHeldFilter}>
            {heldFilterActive ? 'Show all devices' : `Show only the ${summary.wouldBlockCount} affected`}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-zinc-500">{label}</dt>
      <dd className="font-mono font-medium text-zinc-900">{value}</dd>
    </div>
  );
}

// FD-221/FD-223 — which policy is actually in force (FD-ADR-028 R4/R9/R10).
//
// Deliberately an inline strip in the existing panel rather than a new card: the
// operator is already reading this block to decide observe→enforce, and a second
// surface would make provenance something you go and look for rather than something
// you cannot miss.
//
// The three states are MUTUALLY EXCLUSIVE and rendered as one chip plus one
// sentence, because an operator needs to read a single answer, not compare three
// indicators:
//
//   floor   — the FD-ADR-027 baseline policy is evaluating. Checked FIRST, because
//             it is also true when a pin cannot be satisfied: in that state the
//             floor really is what runs, however current the cached copy looks, and
//             a pinned deployment must never read as current.
//   stale   — serving the cached last-known-good. Fluent `warning`, following
//             ReadinessBadge's established precedent for "working, but a different
//             operator action is needed" — not a new colour.
//   current — a live ruleset. Neutral zinc: nothing to act on.
//
// `configured: false` says Track B is not enabled, NOT that something failed. It is
// the shipped default for every deployment, and rendering it as an error state would
// train operators to ignore a real one.
export function RulesetProvenance({ ruleset }: { ruleset?: ReadinessRulesetState | null }) {
  // A Manager predating FD-221 sends no field at all. Render nothing rather than
  // guessing — an invented "unknown" state would be indistinguishable from a real
  // one, which is the failure mode this whole feature exists to prevent.
  if (!ruleset) return null;

  if (!ruleset.configured) {
    return (
      <ProvenanceRow
        icon={<SlidersHorizontal size={16} strokeWidth={1.5} className="text-zinc-500" aria-hidden />}
        chip={<Badge appearance="tint" color="subtle">Local policy</Badge>}
      >
        Remote rulesets are not enabled for this deployment, so verdicts come from the
        locally configured policy.
      </ProvenanceRow>
    );
  }

  if (ruleset.floor) {
    const pinUnsatisfied = ruleset.pinnedVersion !== null && ruleset.pinSatisfied === false;
    return (
      <ProvenanceRow
        icon={<SlidersHorizontal size={16} strokeWidth={1.5} className="text-amber-600" aria-hidden />}
        chip={<Badge appearance="tint" color="warning">Local policy</Badge>}
      >
        {pinUnsatisfied ? (
          <>
            Pinned to <Mono>{ruleset.pinnedVersion}</Mono>, which is not available — verdicts
            are coming from the locally configured policy, not the pinned ruleset.
            {ruleset.rulesetVersion ? (
              <> The cached copy is <Mono>{ruleset.rulesetVersion}</Mono>.</>
            ) : null}
          </>
        ) : (
          <>
            No remote ruleset is available, so verdicts come from the locally configured
            policy. This is the intended fallback, not an outage.
          </>
        )}
      </ProvenanceRow>
    );
  }

  const stale = ruleset.cached && !ruleset.pinnedVersion;

  return (
    <ProvenanceRow
      icon={
        stale ? (
          <Archive size={16} strokeWidth={1.5} className="text-amber-600" aria-hidden />
        ) : (
          <FileCode size={16} strokeWidth={1.5} className="text-zinc-500" aria-hidden />
        )
      }
      chip={
        <>
          <Badge appearance="tint" color={stale ? 'warning' : 'subtle'}>
            {stale ? 'Cached ruleset' : 'Ruleset'}
          </Badge>
          {ruleset.pinnedVersion ? (
            <Badge appearance="outline" color="important">
              <Pin size={12} strokeWidth={1.5} className="mr-1" aria-hidden />
              Pinned
            </Badge>
          ) : null}
        </>
      }
    >
      Verdicts come from ruleset <Mono>{ruleset.rulesetVersion}</Mono>
      {ruleset.ring ? <> on ring <Mono>{ruleset.ring}</Mono></> : null}
      {ruleset.cachedUtc ? <>, retrieved <Mono>{ruleset.cachedUtc}</Mono></> : null}.
      {stale ? ' This is the last copy successfully retrieved — it may no longer be current.' : ''}
    </ProvenanceRow>
  );
}

function ProvenanceRow({
  icon,
  chip,
  children,
}: {
  icon: React.ReactNode;
  chip: React.ReactNode;
  children: React.ReactNode;
}) {
  // border-l accent rather than a card: logic-grouping via a 1px rule keeps the
  // panel one readable block instead of two competing containers.
  return (
    <div className="flex flex-col gap-1 border-l-2 border-zinc-200 pl-3">
      <div className="flex flex-wrap items-center gap-2">
        {icon}
        {chip}
      </div>
      <p className="text-xs text-zinc-600">{children}</p>
    </div>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return <span className="font-mono text-zinc-900">{children}</span>;
}
